/**
 * Archiving an experiment, and what archiving its inputs does to it.
 *
 * A running experiment can't be archived: the ⋯ shows "Can't archive … yet"
 * with Stop instead of the confirm. So does a 409 from archive, when it
 * started in another tab: `experiment_busy`, or the generic `conflict` (or no
 * code) until the backend sends that; archive is idempotent, so a 409 there
 * means busy. An experiment whose
 * dataset or an evaluator is archived can't run again until they are
 * restored: `RestoreToRun` says so, with a Restore for each.
 */

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/overlay";
import { errorText } from "@/components/ui/states";
import { ArchiveDialog } from "@/features/archive/archive-dialog";
import { ArchiveMenu } from "@/features/archive/archive-menu";
import { useRestore } from "@/features/archive/hooks";
import { useMayArchive } from "@/features/archive/may";
import { useProject } from "@/features/projects/hooks";
import { useUiStore } from "@/stores/ui-store";
import { useExperimentAction } from "../hooks";
import { isLive } from "../status";
import type { ExperimentSummary } from "../types";

function BlockedDialog({ experiment: e, onClose }: { experiment: ExperimentSummary; onClose: () => void }) {
  const action = useExperimentAction(e.id);
  const notify = useUiStore((s) => s.notify);
  const mayStop = useProject().can("experiments:write");
  const stop = () =>
    action.mutate("stop", {
      onSuccess: () => (onClose(), notify("Stopping. Archive it once it has stopped.")),
      onError: (err) => notify(errorText(err, "Could not stop it. Try again."), "error"),
    });
  return (
    <Dialog
      title={`Can't archive "${e.name}" yet`}
      onClose={onClose}
      width={480}
      footer={
        <>
          <span className="flex-1" />
          {mayStop && e.status !== "stopping" && (
            <Button disabled={action.isPending} onClick={stop}>
              Stop experiment
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            OK
          </Button>
        </>
      }
    >
      <p className="text-[13px] leading-relaxed text-body">
        {e.status === "stopping"
          ? "It is stopping. Archive it once it has stopped."
          : "It is still running. Stop it, or wait for it to finish, then archive it."}
      </p>
    </Dialog>
  );
}

const BUSY = new Set(["experiment_busy", "conflict"]);

/** The page's ⋯: the plain confirm, or the blocked dialog while it runs. */
export function ExperimentArchiveMenu({
  experiment: e,
  size = "md",
}: {
  experiment: ExperimentSummary;
  size?: "sm" | "md" | undefined;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <ArchiveMenu
      kind="experiment"
      row={e}
      size={size}
      dialog={(close) =>
        busy || isLive(e.status) ? (
          <BlockedDialog experiment={e} onClose={() => (setBusy(false), close())} />
        ) : (
          <ArchiveDialog
            kind="experiment"
            target={e}
            onClose={close}
            onConflict={(err) => (BUSY.has(err.code ?? "conflict") ? (setBusy(true), true) : false)}
          />
        )
      }
    />
  );
}

/** The archived dataset and evaluators that stop this experiment running again. */
export function archivedInputs(e: ExperimentSummary) {
  return {
    dataset: e.dataset?.archive ? e.dataset : null,
    evaluators: e.evaluators.filter((v) => v.archive),
  };
}

export const mayRunAgain = (e: ExperimentSummary) => {
  const { dataset, evaluators } = archivedInputs(e);
  return !dataset && evaluators.length === 0;
};

/** "Restore to run again." with a Restore per archived input, for people who may. */
export function RestoreToRun({ experiment: e }: { experiment: ExperimentSummary }) {
  const { dataset, evaluators } = archivedInputs(e);
  const restoreDataset = useRestore("dataset");
  const restoreEvaluator = useRestore("evaluator");
  const mayDataset = useMayArchive("dataset")();
  const mayEvaluator = useMayArchive("evaluator")();
  if (!dataset && evaluators.length === 0) return null;
  const names = [
    ...(dataset ? [`dataset "${dataset.name}"`] : []),
    ...evaluators.map((v) => `evaluator "${v.name}"`),
  ];
  return (
    <section
      aria-label="Archived inputs"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-[9px] bg-subtle px-4 py-3 text-[13px] leading-normal text-body"
    >
      <p className="min-w-0 flex-1">
        Its {names.join(" and ")} {names.length === 1 ? "is" : "are"} archived. Restore to run again.
        {!mayDataset && !mayEvaluator && (
          <span className="block text-muted">Only editors and owners can restore.</span>
        )}
      </p>
      <span className="flex flex-wrap gap-2">
        {dataset && mayDataset && (
          <Button onClick={() => void restoreDataset(dataset)}>
            {evaluators.length ? "Restore dataset" : "Restore"}
          </Button>
        )}
        {mayEvaluator &&
          evaluators.map((v) => (
            <Button key={v.id} onClick={() => void restoreEvaluator(v)}>
              {dataset || evaluators.length > 1 ? `Restore "${v.name}"` : "Restore"}
            </Button>
          ))}
      </span>
    </section>
  );
}
